import {
  Bell,
  CalendarClock,
  Cpu,
  Gauge,
  Inbox,
  LogOut,
  Menu,
  Moon,
  Radio,
  Send,
  Share2,
  Sun,
} from "lucide-react";
import { Dialog as RadixDialog } from "radix-ui";
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { useLogout, useMe, useOverview } from "@/api/hooks";
import { type MessageKey, useI18n } from "@/i18n";
import { cx } from "@/lib/cx";

import { currentSegment } from "@/lib/time";

import { BrandMark } from "./BrandMark";

interface NavItem {
  to: string;
  label: MessageKey;
  icon: React.ComponentType<{ className?: string }>;
  admin?: boolean;
  badge?: number;
  badgeTone?: "alert" | "plain";
}

function useTheme() {
  const [theme, setTheme] = useState<"light" | "dark" | null>(() => {
    try {
      const saved = localStorage.getItem("s3h.theme");
      return saved === "light" || saved === "dark" ? saved : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (theme) document.documentElement.dataset.theme = theme;
    else delete document.documentElement.dataset.theme;
  }, [theme]);
  const isDark =
    theme === "dark" ||
    (theme === null && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  const toggle = () => {
    const next = isDark ? "light" : "dark";
    setTheme(next);
    try {
      localStorage.setItem("s3h.theme", next);
    } catch {
      /* storage unavailable */
    }
  };
  return { isDark, toggle };
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

function Brand() {
  const { t, fmt } = useI18n();
  const now = useClock();
  return (
    <div className="flex items-center gap-3 text-rail-text">
      {/* The 24-hour day wraps the 12-hour dial: 00–03 and 12–15 both fill 12–3. */}
      <BrandMark
        quadrant={currentSegment(now) % 4}
        size={36}
        land="var(--c-rail-text)"
        cut="var(--c-rail)"
        label={t("shell.markLabel")}
      />
      <div className="leading-tight">
        <p className="font-semibold tracking-[-0.01em]">{t("brand.name")}</p>
        <p className="num text-xs text-rail-muted">
          {t("shell.damascusTime", { time: fmt.time(now) })}
        </p>
      </div>
    </div>
  );
}

function NavList({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const { t, fmt } = useI18n();
  return (
    <ul className="flex flex-col gap-0.5">
      {items.map(({ to, label, icon: Icon, badge, badgeTone }) => (
        <li key={to}>
          <NavLink
            to={to}
            end={to === "/"}
            onClick={onNavigate}
            className={({ isActive }) =>
              cx(
                "group flex h-9 items-center gap-3 rounded-[var(--radius-control)] px-3 text-sm transition-colors",
                isActive
                  ? "bg-rail-2 text-rail-text shadow-[inset_3px_0_0_var(--c-accent)] rtl:shadow-[inset_-3px_0_0_var(--c-accent)]"
                  : "text-rail-muted hover:bg-rail-2 hover:text-rail-text",
              )
            }
          >
            <Icon className="size-4 shrink-0" />
            <span className="flex-1">{t(label)}</span>
            {badge ? (
              <span
                className={cx(
                  "num min-w-5 rounded-full px-1.5 text-center text-xs leading-5",
                  badgeTone === "alert" ? "bg-accent text-on-accent" : "bg-rail-2 text-rail-text",
                )}
              >
                {fmt.number(badge)}
              </span>
            ) : null}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t, lang, setLang } = useI18n();
  const { data: me } = useMe();
  const { data: overview } = useOverview();
  const logout = useLogout();
  const { isDark, toggle } = useTheme();
  const isAdmin = Boolean(me?.is_admin);

  const newsroom: NavItem[] = [
    { to: "/", label: "nav.overview", icon: Gauge },
    { to: "/review", label: "nav.review", icon: Inbox, badge: overview?.drafts_awaiting },
    { to: "/publish-log", label: "nav.publishLog", icon: Send, admin: true },
  ];
  const pipeline: NavItem[] = [
    { to: "/telegram", label: "nav.telegram", icon: Radio, admin: true },
    { to: "/ai-settings", label: "nav.ai", icon: Cpu, admin: true },
    { to: "/schedules", label: "nav.schedules", icon: CalendarClock, admin: true },
    { to: "/platforms", label: "nav.platforms", icon: Share2, admin: true },
    {
      to: "/notifications",
      label: "nav.notifications",
      icon: Bell,
      admin: true,
      badge: overview?.critical_alerts.length,
      badgeTone: "alert",
    },
  ];
  const visible = (items: NavItem[]) => items.filter((item) => !item.admin || isAdmin);

  return (
    <div className="flex h-full flex-col gap-7 bg-rail px-3 py-5">
      <div className="px-2">
        <Brand />
      </div>
      <nav aria-label={t("shell.navLabel")} className="flex flex-1 flex-col gap-6 overflow-y-auto">
        <div>
          <p className="mb-1.5 px-3 text-xs text-rail-muted">{t("nav.groupNewsroom")}</p>
          <NavList items={visible(newsroom)} onNavigate={onNavigate} />
        </div>
        {isAdmin && (
          <div>
            <p className="mb-1.5 px-3 text-xs text-rail-muted">{t("nav.groupPipeline")}</p>
            <NavList items={visible(pipeline)} onNavigate={onNavigate} />
          </div>
        )}
      </nav>
      <div className="flex flex-col gap-3 border-t border-rail-2 px-2 pt-4 text-rail-text">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm">{me?.first_name || me?.username}</p>
            <p className="text-xs text-rail-muted">
              {isAdmin ? t("roles.admin") : t("roles.editor")}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className="h-8 rounded-[var(--radius-control)] px-2 text-sm text-rail-muted hover:bg-rail-2 hover:text-rail-text"
              aria-label={t("shell.switchLanguage")}
              lang={lang === "ar" ? "en" : "ar"}
            >
              {lang === "ar" ? "English" : "العربية"}
            </button>
            <button
              type="button"
              onClick={toggle}
              className="grid size-8 place-items-center rounded-[var(--radius-control)] text-rail-muted hover:bg-rail-2 hover:text-rail-text"
              aria-label={isDark ? t("shell.lightMode") : t("shell.darkMode")}
            >
              {isDark ? <Sun className="size-4" /> : <Moon className="size-4" />}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => logout.mutate()}
          className="flex h-8 items-center gap-2 rounded-[var(--radius-control)] px-1 text-sm text-rail-muted hover:text-rail-text"
        >
          <LogOut className="size-4" />
          {t("shell.signOut")}
        </button>
      </div>
    </div>
  );
}

export function Shell() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[15.5rem_minmax(0,1fr)]">
      <aside className="sticky top-0 hidden h-dvh lg:block">
        <Sidebar />
      </aside>

      <div className="sticky top-0 z-30 flex items-center justify-between bg-rail px-4 py-3 lg:hidden">
        <Brand />
        <RadixDialog.Root open={open} onOpenChange={setOpen}>
          <RadixDialog.Trigger
            className="grid size-9 place-items-center rounded-[var(--radius-control)] text-rail-text hover:bg-rail-2"
            aria-label={t("shell.openMenu")}
          >
            <Menu className="size-5" />
          </RadixDialog.Trigger>
          <RadixDialog.Portal>
            <RadixDialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
            <RadixDialog.Content className="fixed inset-y-0 start-0 z-50 w-72 max-w-[85vw]">
              <RadixDialog.Title className="sr-only">{t("shell.navLabel")}</RadixDialog.Title>
              <RadixDialog.Description className="sr-only">
                {t("shell.navLabel")}
              </RadixDialog.Description>
              <Sidebar onNavigate={() => setOpen(false)} />
            </RadixDialog.Content>
          </RadixDialog.Portal>
        </RadixDialog.Root>
      </div>

      <main className="min-w-0 px-4 py-6 sm:px-8 lg:px-10 lg:py-9">
        <Outlet />
      </main>
    </div>
  );
}
